// src/pages/Game/GameAutoResult.jsx
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

// Convert any UTC date string or timestamp to IST display string
const toIST = (utcValue, includeSeconds = false) => {
  if (!utcValue) return '--';
  const date = new Date(utcValue);
  if (isNaN(date)) return '--';
  return date.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds ? { second: '2-digit' } : {}),
    hour12: true,
  });
};

const GameAutoResult = () => {
  const [loading, setLoading] = useState(false);
  const [updatingPercentage, setUpdatingPercentage] = useState(false);
  const [data, setData] = useState({
    classic: { currentRound: null, upcomingRound: null, recentResults: [] },
    roulette: { currentRound: null, upcomingRound: null, recentResults: [] },
    funtarget: { currentRound: null, upcomingRound: null, recentResults: [] },
    serverTime: null
  });
  const [countdowns, setCountdowns] = useState({ classic: 0, roulette: 0, funtarget: 0 });
  const [settings, setSettings] = useState({
    classic: { winningPercentage: 80, commissionPercentage: 20 },
    roulette: { winningPercentage: 80, commissionPercentage: 20 },
    funtarget: { winningPercentage: 80, commissionPercentage: 20 },
    dynamicDistributionEnabled: true,
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editGame, setEditGame] = useState(null); // 'classic' | 'roulette' | 'funtarget'
  const [editPercentage, setEditPercentage] = useState(80);
  const [editDynamicEnabled, setEditDynamicEnabled] = useState(true);

  useEffect(() => {
    fetchAutoResultData();
    fetchWinningPercentage();
    const interval = setInterval(fetchAutoResultData, 5000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timers for each game
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdowns(prev => {
        const next = { ...prev };
        ['classic', 'roulette', 'funtarget'].forEach(game => {
          if (next[game] > 0) {
            next[game] = next[game] - 1000;
          }
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchAutoResultData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/game/auto-result-data', { params: { limit: 5 } });
      if (response.data?.success) {
        const d = response.data.data;

        // Normalise a game block — rewrite all timeFormatted fields to IST
        const normaliseGame = (game) => {
          if (!game) return { currentRound: null, upcomingRound: null, recentResults: [] };
          return {
            ...game,
            currentRound: game.currentRound ? {
              ...game.currentRound,
              timeFormatted: toIST(game.currentRound.endTime || game.currentRound.startTime),
            } : null,
            upcomingRound: game.upcomingRound ? {
              ...game.upcomingRound,
              timeFormatted: toIST(game.upcomingRound.startTime),
            } : null,
            recentResults: (game.recentResults || []).map(r => ({
              ...r,
              timeFormatted: toIST(r.time),
            })),
          };
        };

        setData({
          classic:    normaliseGame(d.classic),
          roulette:   normaliseGame(d.roulette),
          funtarget:  normaliseGame(d.funtarget),
          serverTime: d.serverTime
            ? { ...d.serverTime, formatted: toIST(d.serverTime.timestamp, true) }
            : null,
        });
        setCountdowns({
          classic: d.classic?.currentRound?.timeRemaining ?? 0,
          roulette: d.roulette?.currentRound?.timeRemaining ?? 0,
          funtarget: d.funtarget?.currentRound?.timeRemaining ?? 0,
        });
      }
    } catch (error) {
      console.error('Error fetching auto result data:', error);
      toast.error('Failed to fetch game data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWinningPercentage = async () => {
    try {
      const response = await api.get('/admin/settings/winning-percentage');
      if (response.data?.success) {
        const d = response.data.data;
        setSettings({
          classic: d.classic,
          roulette: d.roulette,
          funtarget: d.funtarget,
          dynamicDistributionEnabled: d.dynamicDistributionEnabled,
        });
      }
    } catch (error) {
      console.error('Error fetching winning percentage:', error);
    }
  };

  const openEditModal = (game) => {
    setEditGame(game);
    setEditPercentage(settings[game].winningPercentage);
    setEditDynamicEnabled(settings.dynamicDistributionEnabled);
    setShowEditModal(true);
  };

  const handleUpdatePercentage = async () => {
    if (editPercentage < 0 || editPercentage > 100) {
      toast.error('Percentage must be between 0 and 100');
      return;
    }
    setUpdatingPercentage(true);
    try {
      const response = await api.put('/admin/settings/winning-percentage', {
        game: editGame,
        percentage: editPercentage,
        dynamicEnabled: editDynamicEnabled,
      });
      if (response.data?.success) {
        setSettings(prev => ({
          ...prev,
          [editGame]: {
            winningPercentage: editPercentage,
            commissionPercentage: 100 - editPercentage,
          },
          dynamicDistributionEnabled: editDynamicEnabled,
        }));
        setShowEditModal(false);
        toast.success(`${editGame} winning distribution updated to ${editPercentage}%`);
        fetchAutoResultData();
      } else {
        toast.error(response.data?.error || 'Failed to update');
      }
    } catch (error) {
      console.error('Error updating winning percentage:', error);
      toast.error(error.response?.data?.error || 'Failed to update winning percentage');
    } finally {
      setUpdatingPercentage(false);
    }
  };

  const getSuitIcon = (suit) => {
    switch (suit?.toLowerCase()) {
      case 'clubs': return '♣️';
      case 'diamonds': return '♦️';
      case 'hearts': return '♥️';
      case 'spades': return '♠️';
      default: return '';
    }
  };

  const getCardDisplay = (card, suit) => {
    if (!card) return '--';
    return `${card}${getSuitIcon(suit)}`;
  };

  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return '0:00';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // ─── Sub-components ───────────────────────────────────────────────────────

  const DistributionSettings = ({ game, gameLabel }) => {
    const wp = settings[game]?.winningPercentage ?? 80;
    const cp = settings[game]?.commissionPercentage ?? 20;
    return (
      <div className="col-12">
        <div className="winning-distribution-card">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="admin-card-heading-two mb-0">Winning Distribution Settings</h4>
            <button className="btn btn-sm btn-primary" onClick={() => openEditModal(game)}>
              <i className="fas fa-edit me-1"></i> Edit
            </button>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="distribution-stats">
                <div className="stat-item">
                  <div className="stat-label">Winning Distribution</div>
                  <div className="stat-value text-success">{wp}%</div>
                  <div className="stat-description">of total bets go to winners</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Commission</div>
                  <div className="stat-value text-danger">{cp}%</div>
                  <div className="stat-description">platform commission</div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="distribution-mode">
                <div className="mode-badge">
                  {settings.dynamicDistributionEnabled ? (
                    <span className="badge bg-success">Dynamic Distribution Enabled</span>
                  ) : (
                    <span className="badge bg-secondary">Random Distribution Enabled</span>
                  )}
                </div>
                <div className="progress mt-3" style={{ height: '30px' }}>
                  <div className="progress-bar bg-success" style={{ width: `${wp}%` }}>
                    {wp}% Winners
                  </div>
                  <div className="progress-bar bg-danger" style={{ width: `${cp}%` }}>
                    {cp}% Commission
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CurrentRound = ({ game, resultDisplay }) => {
    const round = data[game]?.currentRound;
    const upcomingRound = data[game]?.upcomingRound;
    const wp = settings[game]?.winningPercentage ?? 80;
    return (
      <div className="col-12">
        <h4 className="admin-card-heading-two">Current Round Result</h4>
        <div className="round-card">
          {round ? (
            <>
              <div className="round-info">
                <div className="info-item">
                  <div className="label">Round ID</div>
                  <div className="value">{round.roundId}</div>
                </div>
                <div className="info-item">
                  <div className="label">Time</div>
                  <div className="value">{round.timeFormatted}</div>
                </div>
                <div className="info-item">
                  <div className="label">Time Remaining</div>
                  <div className="value countdown-timer">
                    {formatTimeRemaining(countdowns[game])}
                  </div>
                </div>
                <div className="info-item">
                  <div className="label">Status</div>
                  <div className="status-bubble red">
                    {resultDisplay(round)}
                  </div>
                </div>
              </div>
              {round.roundData && (
                <div className="round-stats mt-3">
                  <div className="row">
                    <div className="col-md-3">
                      <small>Total Bets</small>
                      <strong>{round.roundData.totalBets || 0}</strong>
                    </div>
                    <div className="col-md-3">
                      <small>Total Amount</small>
                      <strong>₹{round.roundData.totalBetAmount || 0}</strong>
                    </div>
                    <div className="col-md-3">
                      <small>Carry Forward</small>
                      <strong>₹{round.roundData.carryForwardAmount || 0}</strong>
                    </div>
                    <div className="col-md-3">
                      <small>Max Payout ({wp}%)</small>
                      <strong>
                        ₹{((round.roundData.totalBetAmount || 0) * wp / 100).toFixed(2)}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <p className="mb-0">No active round at the moment</p>
              {upcomingRound && (
                <small className="text-muted">
                  Next round starts at {upcomingRound.timeFormatted}
                </small>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const RecentResults = ({ game, renderCircle }) => {
    const results = data[game]?.recentResults || [];
    return (
      <div className="col-12">
        <h4 className="admin-card-heading-two">Last 5 Results</h4>
        <div className="result-row">
          {loading ? (
            <div className="text-center w-100 py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : results.length > 0 ? (
            results.map((result, index) => (
              <div key={index} className="result-box">
                <div className="result-circle">{renderCircle(result)}</div>
                <div className="result-time">{result.timeFormatted}</div>
                <div className="result-round-id small text-muted">{result.roundId}</div>
              </div>
            ))
          ) : (
            <div className="text-center w-100 py-3">No results available</div>
          )}
        </div>
      </div>
    );
  };

  const GameSection = ({ title, game, resultDisplay, renderCircle }) => (
    <div className="admin-card-one mb-3">
      <div className="admin-card-header">
        <h2 className="admin-card-heading">{title}</h2>
      </div>
      <div className="admin-card-body">
        <div className="row g-4">
          <DistributionSettings game={game} />
          <CurrentRound game={game} resultDisplay={resultDisplay} />
          <RecentResults game={game} renderCircle={renderCircle} />
          {data.serverTime && (
            <div className="col-12 text-center mt-3">
              <small className="text-muted">Server Time: {data.serverTime.formatted}</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="game-auto-result-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Auto Result</h1>
        </div>

        {/* RoyalKing / Classic */}
        <GameSection
          title="RoyalKing Live Game Result"
          game="classic"
          resultDisplay={(round) =>
            round.result?.card
              ? getCardDisplay(round.result.card, round.result.suit)
              : '---'
          }
          renderCircle={(result) => getCardDisplay(result.card, result.suit)}
        />

        {/* Roulette */}
        <GameSection
          title="Roulete Live Game Result"
          game="roulette"
          resultDisplay={(round) =>
            round.result?.result != null ? round.result.result : '---'
          }
          renderCircle={(result) => result.result ?? '--'}
        />

        {/* Fun Target */}
        <GameSection
          title="Fun Target Live Game Result"
          game="funtarget"
          resultDisplay={(round) =>
            round.result?.result != null ? round.result.result : '---'
          }
          renderCircle={(result) => result.result ?? '--'}
        />
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Edit Winning Distribution — {editGame?.charAt(0).toUpperCase() + editGame?.slice(1)}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Winning Distribution Percentage</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={editPercentage}
                      onChange={(e) => setEditPercentage(parseFloat(e.target.value))}
                      min="0"
                      max="100"
                      step="1"
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <small className="text-muted">
                    Percentage of total bets that will be distributed to winners (rest is commission)
                  </small>
                  <div className="mt-2">
                    <div className="progress" style={{ height: '20px' }}>
                      <div className="progress-bar bg-success" style={{ width: `${editPercentage}%` }}>
                        {editPercentage}% Winners
                      </div>
                      <div className="progress-bar bg-danger" style={{ width: `${100 - editPercentage}%` }}>
                        {100 - editPercentage}% Commission
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="dynamicEnabled"
                      checked={editDynamicEnabled}
                      onChange={(e) => setEditDynamicEnabled(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="dynamicEnabled">
                      Enable Dynamic Distribution
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdatePercentage}
                  disabled={updatingPercentage}
                >
                  {updatingPercentage ? 'Updating...' : 'Update Settings'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameAutoResult;